import type { CSSProperties, HTMLAttributes, MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "../core/Avatar";
import { Badge, type BadgeTone } from "../core/Badge";

/**
 * TheCore Studio — ClassCard.
 * A single bookable session row: time rail · title · coach ·
 * status + CTA. Composes `Badge` and `Avatar`.
 */
export type ClassCardStatus =
  | "open"
  | "full"
  | "booked"
  | "closed"
  | "notOpen";

export interface ClassCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "style"> {
  /** Start time, e.g. "06:30". */
  time?: string;
  duration?: string;
  title?: string;
  coach?: string;
  coachSrc?: string;
  spotsLeft?: number;
  /** @default 'open' */
  status?: ClassCardStatus;
  /**
   * Teacher-assigned lesson (issue #183). A booked `assigned` lesson
   * reads "Enrolled" rather than "Booked", since the teacher enrolled
   * the student rather than the student self-booking.
   */
  assigned?: boolean;
  /** Accent color for the CTA. @default 'var(--brand)' */
  accent?: string;
  /** Disable the CTA (e.g. while offline) — overrides status styling. */
  disabled?: boolean;
  /** Native tooltip for the CTA, e.g. "why is this disabled". */
  ctaTitle?: string;
  /** Called when the user taps the CTA. The original event is passed
   *  in so the caller can call `stopPropagation()` to keep a parent
   *  click handler (e.g. row navigation) from also firing. */
  onAction?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** Optional content rendered inside the card, below the main row.
   *  Use it for secondary actions (e.g. "Cancel reservation") so they
   *  stay within the card bounds instead of overflowing it. */
  footer?: ReactNode;
  style?: CSSProperties;
}

const STATUS_TONE: Record<ClassCardStatus, BadgeTone> = {
  open: "success",
  full: "danger",
  booked: "brand",
  closed: "neutral",
  notOpen: "neutral",
};

export function ClassCard({
  time = "06:30",
  duration = "45 min",
  title = "Rhythm Ride",
  coach = "Mara Voss",
  coachSrc,
  spotsLeft = 4,
  status = "open",
  assigned = false,
  accent = "var(--brand-hover)", /* terracotta-600 resting shade (AA on white) */
  disabled = false,
  ctaTitle,
  onAction,
  footer = null,
  style = {},
  ...rest
}: ClassCardProps) {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status] ?? STATUS_TONE.open;
  const statusLabel =
    status === "open"
      ? t("classCard.open", { count: spotsLeft })
      : assigned && status === "booked"
        ? t("classCard.enrolled")
        : t(`classCard.${status}`);
  const cta =
    status === "open"
      ? t("classCard.ctaBook")
      : status === "full"
        ? t("common.classFull")
        : status === "closed" || status === "notOpen"
          ? t("classCard.ctaClosed")
          : assigned
            ? t("classCard.ctaEnrolled")
            : t("classCard.ctaBooked");
  // A full class has no waitlist yet (#270): the CTA is inert until it does.
  const isInert =
    disabled ||
    status === "booked" ||
    status === "closed" ||
    status === "notOpen" ||
    status === "full";

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: "var(--space-4)",
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm)",
        padding: "var(--space-4)",
        ...style,
      }}
      {...rest}
    >
      {/* time rail */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 64,
          paddingRight: "var(--space-4)",
          borderRight: "1px solid var(--border-subtle)",
        }}
      >
        <span
          style={{
            font: "var(--fw-bold) var(--fs-lg)/1 var(--font-mono)",
            color: "var(--text-strong)",
          }}
        >
          {time}
        </span>
        <span
          style={{
            font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
            color: "var(--text-faint)",
            marginTop: 4,
          }}
        >
          {duration}
        </span>
      </div>

      {/* body */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            style={{
              font: "var(--fw-bold) var(--fs-lg)/1.1 var(--font-display)",
              color: "var(--text-strong)",
            }}
          >
            {title}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar name={coach} src={coachSrc} size={24} />
          <span
            style={{
              font: "var(--fw-medium) var(--fs-sm)/1 var(--font-body)",
              color: "var(--text-muted)",
            }}
          >
            {coach}
          </span>
        </div>
      </div>

      {/* action rail */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <Badge tone={tone} dot>
          {statusLabel}
        </Badge>
        <button
          type="button"
          onClick={onAction}
          title={ctaTitle}
          disabled={isInert}
          style={{
            height: 38,
            padding: "0 18px",
            borderRadius: "var(--radius-pill)",
            border: status === "booked" ? "1px solid var(--border-default)" : "none",
            background:
              status === "booked"
                ? "transparent"
                : status === "closed" || status === "notOpen" || status === "full"
                  ? "var(--surface-muted)"
                  : accent,
            color:
              isInert ? "var(--text-muted)" : "#fff",
            font: "var(--fw-semibold) var(--fs-sm)/1 var(--font-body)",
            cursor: isInert ? "default" : "pointer",
            boxShadow: status === "open" ? "var(--shadow-brand)" : "none",
            whiteSpace: "nowrap",
            transition: "filter var(--dur-fast) var(--ease-out)",
          }}
          onMouseEnter={(e) => {
            if (!isInert) {
              e.currentTarget.style.filter = "brightness(0.96)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.filter = "none";
          }}
        >
          {cta}
        </button>
      </div>

      {footer ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}
